import React, { useMemo } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { authService } from '../../services/api';
import GameGuideTopicGrid from '../../components/game/GameGuideTopicGrid';
import { getBuiltInGuideCategories, cmsSlugOverlapsBuiltIn } from '../../content/gameGuideBuiltIn';
import type { GamePageContent } from '../../content/gameInfo';
import type { GuideTreeCategory } from '../../types';
import { guideText } from '../../utils/guideLocale';

type OutletCtx = {
    tree: GuideTreeCategory[];
    loading: boolean;
    content: GamePageContent;
};

const GameGuideHome: React.FC = () => {
    const { locale } = useLanguage();
    const isAuthenticated = authService.isAuthenticated();
    const { tree, loading, content } = useOutletContext<OutletCtx>();

    const builtInCategories = useMemo(() => getBuiltInGuideCategories(locale), [locale]);

    const extraCmsCategories = useMemo(
        () =>
            tree
                .map((cat) => ({
                    ...cat,
                    articles: cat.articles.filter((a) => !cmsSlugOverlapsBuiltIn(a.slug)),
                }))
                .filter((cat) => cat.articles.length > 0),
        [tree]
    );

    const labels =
        locale === 'pt-BR'
            ? {
                  intro: 'Escolha um tópico abaixo ou no menu ao lado para ler o guia completo.',
                  read: 'Abrir',
                  faq: 'Perguntas frequentes',
                  faqHint: 'Respostas rápidas — ou abra o tópico FAQ para ver tudo.',
                  cmsTitle: 'Mais tutoriais',
                  quickFaq: 'Ver todas as perguntas',
              }
            : {
                  intro: 'Choose a topic below or in the side menu to read the full guide.',
                  read: 'Open',
                  faq: 'FAQ',
                  faqHint: 'Quick answers — or open the FAQ topic to see everything.',
                  cmsTitle: 'More tutorials',
                  quickFaq: 'View all questions',
              };

    const mountGuideCard =
        locale === 'pt-BR'
            ? {
                  title: 'Guia de montarias',
                  summary: 'Regiões, familiares domáveis, estatísticas e mapas de captura.',
                  read: 'Abrir guia',
              }
            : {
                  title: 'Mount guide',
                  summary: 'Regions, tameable fellows, stats and capture maps.',
                  read: 'Open guide',
              };

    return (
        <div className="game-main-inner">
            <p className="game-intro">{labels.intro}</p>

            <section className="game-category-block mount-guide-home-card">
                <h2>{mountGuideCard.title}</h2>
                <ul className="game-topic-grid">
                    <li>
                        <Link to="/game/montarias" className="game-topic-card">
                            <span className="game-topic-card-icon" aria-hidden>
                                <i className="ri-dragon-line" />
                            </span>
                            <div className="game-topic-card-body">
                                <h3>{mountGuideCard.title}</h3>
                                <p>{mountGuideCard.summary}</p>
                                <span className="game-topic-card-action">
                                    {mountGuideCard.read}
                                    <i className="ri-arrow-right-line" />
                                </span>
                            </div>
                        </Link>
                    </li>
                </ul>
            </section>

            {builtInCategories.map((cat) => (
                <section key={cat.key} className="game-category-block">
                    <h2>{cat.title}</h2>
                    <GameGuideTopicGrid topics={cat.topics} readLabel={labels.read} />
                </section>
            ))}

            {!loading && extraCmsCategories.length > 0 && (
                <section className="game-category-block game-category-block--cms">
                    <h2>{labels.cmsTitle}</h2>
                    <ul className="game-article-cards">
                        {extraCmsCategories.map((cat) =>
                            cat.articles.map((art) => (
                                <li key={art.id}>
                                    <Link to={`/game/${art.slug}`} className="game-article-card">
                                        <span className="game-article-card-meta">
                                            {guideText(locale, cat.titleEn, cat.titlePtBr)}
                                        </span>
                                        <h3>{guideText(locale, art.titleEn, art.titlePtBr)}</h3>
                                        <p>
                                            {guideText(locale, art.summaryEn, art.summaryPtBr) ||
                                                guideText(locale, art.titleEn, art.titlePtBr)}
                                        </p>
                                        <span className="game-article-card-link">
                                            {labels.read} <i className="ri-arrow-right-line" />
                                        </span>
                                    </Link>
                                </li>
                            ))
                        )}
                    </ul>
                </section>
            )}

            {content.faq.length > 0 && (
                <section className="game-faq game-faq--home">
                    <div className="game-faq-home-header">
                        <div>
                            <h2>{labels.faq}</h2>
                            <p className="game-faq-hint">{labels.faqHint}</p>
                        </div>
                        <Link to="/game/faq" className="btn btn-outline btn-sm">
                            {labels.quickFaq} <i className="ri-arrow-right-line" />
                        </Link>
                    </div>
                    <div className="game-faq-list">
                        {content.faq.map((item, i) => (
                            <details key={i} className="game-faq-item">
                                <summary>{item.q}</summary>
                                <p>{item.a}</p>
                            </details>
                        ))}
                    </div>
                </section>
            )}

            <section className="game-cta">
                <h2>{content.cta.title}</h2>
                <div className="game-cta-buttons">
                    <Link to="/download" className="btn btn-primary btn-xl">
                        {content.cta.download}
                    </Link>
                    {!isAuthenticated && (
                        <Link to="/register" className="btn btn-outline btn-xl">
                            {content.cta.register}
                        </Link>
                    )}
                </div>
            </section>
        </div>
    );
};

export default GameGuideHome;
